import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card, Col, Form, Row } from "react-bootstrap";
import { push } from "store/actions/router";
import { debounce, find, isEmpty } from "lodash";
import { searchAllUsers } from "actions/adminActions";
import { createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { FiUsers } from "react-icons/fi";
import { Link } from "react-router-dom";

import CopyIcon from "components/CopyIcon";
import IconMessageCard from "components/IconMessageCard";
import IconSpinner from "components/IconSpinner";
import Paginator from "components/Paginator";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import { ROTH_IRA, TRADITIONAL_IRA } from "statics/accountTypes";
import { IndividualApplicationDenied } from "statics/states";
import type { RootState } from "types/state";

import "./Users.scss";

/** One entry of `state.admin.foundUsers` (typed `unknown[]` in the slice),
 *  shaped to the `searchUsers` query selection this page reads. */
interface FoundUser {
  userInfo: { id: string; firstName: string; lastName: string };
  contactInfo: { email: string };
  accounts: Array<{ accountNumber: string; type: string }>;
  kycCurrentState: string | null;
}

/** Row built by `_getUsersData` and rendered by IconTable. */
interface UserRow {
  id: string;
  fullName: string;
  email: string;
  apex: string;
  isDenied: boolean;
  userInfo: FoundUser["userInfo"];
}

const isLoadingSelector = createLoadingSelector(adminConstants.SEARCH_USERS);

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: isLoadingSelector(state),
    users: state.admin.foundUsers,
    usersCount: state.admin.foundUsersCount,
  };
};

const mapDispatchToProps = {
  push,
  searchAllUsers,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = WithApolloClient<ConnectedProps<typeof connector>>;

interface State {
  limit: number;
  offset: number;
  search: string;
  page: number;
}

class Users extends React.PureComponent<Props, State> {
  _textInput: HTMLInputElement | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      limit: 50,
      offset: 0,
      search: "",
      page: 0,
    };
  }

  _onPageChange = ({ selected }: { selected: number }) => {
    const offset = selected * this.state.limit;

    this.setState({ page: selected, offset }, () => this._searchUsers());
  };

  _searchUsers = () => {
    return this.props.searchAllUsers(
      this.props.client as ApolloClient<object>,
      {
        limit: this.state.limit,
        offset: this.state.offset,
        search: this.state.search,
      }
    );
  };

  _onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState(
      {
        search: e.target.value,
        offset: 0,
        page: 0,
      },
      () => this._searchUsers()
    );
  };

  _debounceOnChange = debounce(this._onSearchChange, 500);

  _getUsersData = (): UserRow[] => {
    // state.admin.foundUsers is typed unknown[]; cast to the query selection
    return (this.props.users as FoundUser[]).map((user) => {
      const { userInfo, contactInfo, accounts, kycCurrentState } = user;
      const apexAccount = find(accounts, (account) => {
        return account.type === TRADITIONAL_IRA || account.type === ROTH_IRA;
      });

      const fullName = `${userInfo.firstName} ${userInfo.lastName}`;
      const apex = apexAccount ? apexAccount.accountNumber : "";

      return {
        id: userInfo.id,
        fullName,
        email: contactInfo.email,
        apex,
        isDenied: kycCurrentState === IndividualApplicationDenied,
        userInfo,
      };
    });
  };

  _buildUsersColumns = (): Array<IconTableColumn<UserRow>> => {
    const columns: Array<IconTableColumn<UserRow>> = [
      {
        label: "User Id",
        key: "id",
        customComponent: (props: UserRow) => (
          <>
            {props.id}
            <CopyIcon copyText={props.id} label="User ID" />
          </>
        ),
      },

      {
        label: "User Name",
        key: "fullName",
      },
      {
        label: "Email",
        key: "email",
      },

      {
        label: "Apex Id",
        key: "apex",
        customComponent: (props: UserRow) =>
          props.isDenied ? (
            <span className="user-denied-pill">Denied</span>
          ) : (
            <>{props.apex}</>
          ),
      },
      {
        customComponent: (props: UserRow) => {
          return (
            <Link to={`/users/${props.userInfo.id}`} className="icon-link">
              View Info
            </Link>
          );
        },
      },
    ];
    return columns;
  };

  _clearFilters = () => {
    // ref is assumed set at runtime — preserve original unguarded access
    this._textInput!.value = "";
    this.setState(
      {
        search: "",
        offset: 0,
      },
      () => this._searchUsers()
    );
  };

  render() {
    const showBlankSlate =
      !this.props.isFetching &&
      isEmpty(this.props.users) &&
      !!this.state.search;

    const hideTable =
      this.props.isFetching || showBlankSlate || !this.state.search;

    const columns = this._buildUsersColumns();
    const data = this._getUsersData();

    return (
      <div className="mega-container" id={"admin-users-page"}>
        <section className="page-title-wrap">
          <article className="text-cell">
            <h1 className="page-title">Users</h1>

            <div className="main-content">
              <Card>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <span>
                        <IconHeader
                          variant="labelHeader"
                          headerText="Search User Info"
                        />
                        <IconSubheader
                          subheader="Search by name, id, email, apex id or any user profile
                          information"
                        />
                      </span>
                      <div className="search-bar" style={{ paddingTop: 15 }}>
                        <Form.Control
                          name="search"
                          placeholder="search"
                          onChange={this._debounceOnChange}
                          ref={(node: HTMLInputElement | null) => {
                            this._textInput = node;
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
                {this.props.isFetching && <IconSpinner centered />}

                {showBlankSlate && (
                  <IconMessageCard
                    header={
                      "There were no users found matching your search or filters."
                    }
                    subheader={"Would you like to clear your search?"}
                    icon={<FiUsers color="white" stroke="#60A4BF" size={16} />}
                    actionText={"Clear Filters"}
                    onClick={this._clearFilters}
                  />
                )}

                {!hideTable && (
                  <>
                    <IconTable columns={columns} data={data} />
                    <div
                      style={{
                        padding: 20,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Paginator
                        onChange={this._onPageChange}
                        pageCount={this.props.usersCount / this.state.limit}
                        page={this.state.page}
                      />
                    </div>
                  </>
                )}
              </Card>
            </div>
          </article>
        </section>
      </div>
    );
  }
}

export default connector(withApollo<Props>(Users));
